import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { fetchJournalEntries, sortNewestFirst, JOURNAL_FALLBACK } from "@/lib/journal";

const STORAGE_KEY = "roadrig.latestBuild.dismissed";

export const LatestBuildBanner = () => {
  const [entry, setEntry] = useState(() => sortNewestFirst(JOURNAL_FALLBACK)[0]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchJournalEntries().then((data) => {
      if (!mounted) return;
      const latest = sortNewestFirst(data)[0];
      if (latest) setEntry(latest);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!entry) return;
    const dismissedFor = localStorage.getItem(STORAGE_KEY);
    if (dismissedFor === (entry.slug || entry.id)) return;
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, [entry]);

  const onDismiss = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    localStorage.setItem(STORAGE_KEY, entry.slug || entry.id);
    setVisible(false);
  };

  const onScrollToJournal = (e) => {
    e?.preventDefault?.();
    const el = document.querySelector("#journal");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!entry) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          data-testid="latest-build-banner"
          initial={{ y: -64, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -64, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-[68px] md:top-[78px] left-0 right-0 z-40"
        >
          <div className="mx-3 md:mx-6 max-w-7xl xl:mx-auto">
            <div
              role="button"
              tabIndex={0}
              onClick={onScrollToJournal}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onScrollToJournal(e); }}
              data-testid="latest-build-cta"
              className="group w-full flex items-center gap-3 md:gap-4 px-3 md:px-5 py-2.5 md:py-3 bg-[#141417]/95 backdrop-blur-xl border border-[#F47A20]/30 rounded-sm hover:border-[#F47A20] transition-all text-left shadow-[0_8px_24px_-12px_rgba(244,122,32,0.4)] cursor-pointer"
            >
              <div className="hidden sm:block relative w-12 h-12 md:w-14 md:h-14 overflow-hidden rounded-sm border border-white/10 flex-shrink-0">
                <img
                  src={entry.image_url}
                  alt=""
                  style={{ objectPosition: entry.object_pos || "50% 50%" }}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#F47A20] opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F47A20]" />
                </span>
                <span data-testid="latest-build-new-pulse" className="serial text-[9px] md:text-[10px] tracking-[0.25em] text-[#F47A20] font-bold">NEW</span>
              </div>

              <div className="hidden md:block h-7 w-px bg-white/10" />

              <div className="flex-1 min-w-0">
                <div className="serial text-[9px] md:text-[10px] text-white/45 leading-none mb-0.5">
                  LATEST FROM THE BUILD · {(entry.date_label || "").toUpperCase()}
                </div>
                <div data-testid="latest-build-title" className="text-white text-sm md:text-base font-semibold truncate group-hover:text-[#F47A20] transition-colors">
                  {entry.title}
                </div>
              </div>

              <div className="flex-shrink-0 flex items-center gap-1.5 md:gap-2 text-[#F47A20]">
                <span className="serial text-[9px] md:text-[10px] hidden md:inline">READ</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </div>

              <button
                type="button"
                onClick={onDismiss}
                data-testid="latest-build-dismiss"
                aria-label="Dismiss build update"
                className="flex-shrink-0 w-7 h-7 grid place-items-center text-white/40 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};
